import { css } from '@emotion/react';
import { type FC } from 'react';
import {
  type DropdownOption, DropdownPlacement, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { InputSize } from '~/_shared/baseComponents/inputs';
import { PaginationGoToPageComponent } from '~/_shared/components/pagination/goToPage/paginationGoToPage.component';
import { PaginationComponent } from '~/_shared/components/pagination/pagination.component';
import { PaginationViewInfoComponent } from '~/_shared/components/pagination/paginationViewInfo.component';
import { mq } from '~/_shared/style.mixins';
import type { Pagination } from '~/_shared/types/pagination/pagination';
import { noop } from '~/_shared/utils/function.helpers';
import { notUndefined } from '~/_shared/utils/typeGuards';
import { DataTableBadLocationsComponent } from '~/data/table/dataTableBadLocations.component';

const GOTO_LABEL_MQ_BREAKPOINT = 900;
const LIMIT_NECESSARY_MQ_BREAKPOINT = 490;
const START_WRAPPING_MQ_BREAKPOINT = 820;
const ALL_ITEMS_FIT_INLINE_MQ_BREAKPOINT = 1300;
const ROW_VERTICAL_PADDING = 10;

const wrapperStyle = css({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  width: '100%',
  [mq(START_WRAPPING_MQ_BREAKPOINT)]: {
    justifyContent: 'space-between',
  },
});

const centerStyle = css({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
});

const goToPageStyle = css({
  display: 'none',
  [mq(START_WRAPPING_MQ_BREAKPOINT)]: {
    display: 'block',
  },
});

const goToLabelStyle = css({
  display: 'none',
  [mq(GOTO_LABEL_MQ_BREAKPOINT)]: {
    display: 'block',
  },
});

const paginationStyle = css({
  margin: '0 10px',
  paddingBottom: 4,
  paddingTop: ROW_VERTICAL_PADDING,
  [mq(START_WRAPPING_MQ_BREAKPOINT)]: {
    paddingBottom: ROW_VERTICAL_PADDING,
  },
});

const viewFromToOfStyle = (isStandalone: boolean) => css({
  margin: '0 10px',
  padding: isStandalone ? '2px 0 18px 0' : undefined,
  [mq(START_WRAPPING_MQ_BREAKPOINT)]: {
    marginLeft: isStandalone ? 'auto' : undefined,
  },
});

const perPageDropdownStyle = css({
  display: 'none',
  [mq(LIMIT_NECESSARY_MQ_BREAKPOINT)]: {
    marginRight: 10,
    display: 'block',
  },
  [mq(ALL_ITEMS_FIT_INLINE_MQ_BREAKPOINT)]: {
    margin: '0 10px',
  },
});

const badLocationsTriggerStyle = (isStandalone: boolean) => css({
  display: 'flex',
  justifyContent: 'center',
  marginTop: ROW_VERTICAL_PADDING,
  textAlign: 'right',
  width: '100%',
  [mq(START_WRAPPING_MQ_BREAKPOINT)]: {
    justifyContent: 'end',
    marginTop: isStandalone ? ROW_VERTICAL_PADDING : 0,
  },
});

const perPageDropdownTriggerStyle = css({
  paddingRight: 0,
});

type DataTableBadLocationProps = Readonly<{
  badLocationsCount?: number;
  onBadLocationsClick: (() => void);
}>;

export type DataTablePaginationProps = Readonly<{
  pagination: Pagination;
  perPageOptions?: ReadonlyArray<DropdownOption<number>>;

  onPageSelect: (selectedValue: number) => void;
  onPerPageChange: ((newPerPage: number) => void);
}>;

type DataTableBottomSectionProps = Readonly<{
  badDataProps?: DataTableBadLocationProps;
  hasRows: boolean;
  paginationProps?: DataTablePaginationProps;
}>;

export const DataTableBottomSectionComponent: FC<DataTableBottomSectionProps> = ({
  badDataProps,
  hasRows,
  paginationProps,
}) => {
  const hasPagination = !!paginationProps && paginationProps.pagination.lastPage > 1;

  return (
    <div css={wrapperStyle}>
      {hasPagination && (
        <div css={goToPageStyle}>
          <PaginationGoToPageComponent
            currentPage={paginationProps.pagination.currentPage}
            labelStyle={goToLabelStyle}
            onPageSelect={paginationProps.onPageSelect ?? noop}
            pageCount={paginationProps.pagination.lastPage}
          />
        </div>
      )}

      {hasPagination && (
        <span css={centerStyle}>
          <PaginationComponent
            css={paginationStyle}
            currentPage={paginationProps.pagination.currentPage}
            onPageSelect={paginationProps.onPageSelect ?? noop}
            pageCount={paginationProps.pagination.lastPage}
          />
          {paginationProps.perPageOptions && (
            <RegularDropdownComponent
              css={perPageDropdownStyle}
              disableAutoHeight
              dropdownPlacement={DropdownPlacement.Top}
              inPortal
              inputSize={InputSize.Small}
              onChange={paginationProps.onPerPageChange ?? noop}
              options={paginationProps.perPageOptions}
              triggerStyle={perPageDropdownTriggerStyle}
              value={paginationProps.pagination.perPage}
            />
          )}
        </span>
      )}

      {hasRows && paginationProps && (
        <PaginationViewInfoComponent
          css={viewFromToOfStyle(!hasPagination)}
          pagination={paginationProps.pagination}
        />
      )}

      {notUndefined(badDataProps?.badLocationsCount) && (
        <DataTableBadLocationsComponent
          css={badLocationsTriggerStyle(!hasRows)}
          badLocationsCount={badDataProps.badLocationsCount}
          onBadLocationsClick={badDataProps.onBadLocationsClick}
        />
      )}
    </div>
  );
};
